import { nodeConfig } from '@scripts/mixins';
import { getVideoAPIByType } from '@scripts/modules/api/video';
import { appendScriptToBody, getMotionPreference, getDataPreference } from '@scripts/helpers/utils';
import * as CONSTANTS from '@scripts/helpers/constants';

const NAMESPACE = 'video-lazy';
const DEFAULT_SELECTOR = '[data-js-video-lazy]';

const PAUSE_EVENTS = ['accordion:close', 'carousel:slideHidden', 'disclosure:close', 'modal:close', 'tabs:change'];

/**
 * @typedef {Object} LazyVideoSettings
 * @property {boolean} [autoplay=false] - Autoplay muted on scroll.
 * @property {string} [rootMargin='200px'] - Observer margin for script loading.
 * @property {number} [volume=25] - Default playback volume (0-100).
 */
const DEFAULT_SETTINGS = {
	autoplay: false,
	rootMargin: '200px',
	volume: 25
};

/**
 * Lazy video player. Facade method to load third-party players.
 * @fires video-lazy:play
 * @fires video-lazy:pause
 * @fires video-lazy:ready
 */
class LazyVideo {
	/**
	 * @param {HTMLElement} node
	 * @param {LazyVideoSettings} config
	 */
	constructor(node, config) {
		this.elNode = node;
		this.config = config;
		this.elIframeTarget = this.elNode.querySelector(`[data-js-${NAMESPACE}-iframe-target]`);
		this.elPlayBtn = this.elNode.querySelector(`[data-js-${NAMESPACE}-play]`);

		this.api = null;
		this.scriptLoaded = false;
		this.observer = null;
	}

	init() {
		if (!this.api) throw Error(`[${NAMESPACE}] API must be provided via setApi()`);

		this._setup();
		this._bindUI();
	}

	destroy() {
		this._unbindUI();
		this.observer?.disconnect();
		this.elNode.classList.remove(CONSTANTS.LOADED_CLASS);
		this.elPlayBtn?.removeAttribute('role');
	}

	setApi(api) {
		this.api = api;
	}

	play() {
		this._dispatch('play');
		return Promise.resolve(this.api.play());
	}

	pause() {
		this.api.pause();
		this._dispatch('pause');
	}

	muteAndPlay() {
		this.elNode.classList.add(CONSTANTS.LOADED_CLASS);
		this.api.setVolumeByPercentage(0);
		this.play();
	}

	playWithAudio(volumePercentage = this.config.volume) {
		this.elNode.classList.add(CONSTANTS.LOADED_CLASS);
		this.api.setVolumeByPercentage(volumePercentage);
		this.play();
	}

	loadPlayer() {
		if (!this.scriptLoaded) return Promise.reject("Script hasn't loaded");

		const config = { ...this.config, iframe: this.elIframeTarget };

		return this.api.loadPlayer(config).then(() => this._dispatch('ready'));
	}

	_setup() {
		this.elPlayBtn?.setAttribute('role', 'button');
	}

	_bindUI() {
		this._onPlayBtnClick = this._onPlayBtnClick.bind(this);
		this._onPlayBtnKeydown = this._onPlayBtnKeydown.bind(this);
		this._onGlobalStateChange = this._onGlobalStateChange.bind(this);

		this.observer = new window.IntersectionObserver(
			([entry]) => {
				if (!entry.isIntersecting) return;

				this._onIntersecting();
				this.observer.unobserve(entry.target);
			},
			{ rootMargin: this.config.rootMargin }
		);

		this.observer.observe(this.elNode);

		if (!this.config.autoplay && this.elPlayBtn) {
			this.elPlayBtn.addEventListener('click', this._onPlayBtnClick);
			this.elPlayBtn.addEventListener('keydown', this._onPlayBtnKeydown);
		}

		PAUSE_EVENTS.forEach((evt) => {
			document.addEventListener(evt, this._onGlobalStateChange);
		});
	}

	_unbindUI() {
		if (this.elPlayBtn) {
			this.elPlayBtn.removeEventListener('click', this._onPlayBtnClick);
			this.elPlayBtn.removeEventListener('keydown', this._onPlayBtnKeydown);
		}

		PAUSE_EVENTS.forEach((evt) => {
			document.removeEventListener(evt, this._onGlobalStateChange);
		});
	}

	/** @param {string} action */
	_dispatch(action) {
		this.elNode.dispatchEvent(
			new CustomEvent(`${NAMESPACE}:${action}`, {
				bubbles: true,
				detail: { instance: this }
			})
		);
	}

	_onIntersecting() {
		const { autoplay } = this.config;
		const shouldAutoplay = autoplay && !getMotionPreference() && !getDataPreference();

		let pipeline = appendScriptToBody(this.api.scriptUrl).then(() => {
			this.scriptLoaded = true;
		});

		if (!shouldAutoplay) return pipeline.catch(() => {});

		return pipeline.then(() => this.loadPlayer()).then(() => this.muteAndPlay()).catch(() => {});
	}

	_onPlayBtnClick(event) {
		event.preventDefault();
		this.elPlayBtn.removeEventListener('click', this._onPlayBtnClick);

		return this.loadPlayer().then(() => {
			this.playWithAudio();
			this._focusPlayer();
		});
	}

	_onPlayBtnKeydown(event) {
		if (event.key === ' ' || event.key === 'Spacebar') {
			event.preventDefault();
			this.elPlayBtn.click();
		}
	}

	_focusPlayer() {
		this.elIframeTarget?.querySelector('iframe')?.focus();
	}

	/** Pauses only if this video is inside the affected container or slide. */
	_onGlobalStateChange(event) {
		const container = event.detail?.slide || event.target;

		if (!container.contains(this.elNode)) return;

		if (!this.elNode.classList.contains(CONSTANTS.LOADED_CLASS)) return;

		this.pause();
	}
}

export default function factory(selector = DEFAULT_SELECTOR, settings = {}) {
	const listNode = document.querySelectorAll(selector);

	if (!listNode.length) return [];

	return Array.from(listNode)
		.map((node) => {
			const config = nodeConfig(node, NAMESPACE, settings, DEFAULT_SETTINGS);

			if (!config.id || !config.type) return;

			const apiCtr = getVideoAPIByType(config.type);

			if (!apiCtr) {
				node.style.display = 'none';
				return;
			}

			const module = new LazyVideo(node, config);

			module.setApi(new apiCtr());
			module.init();

			return module;
		})
		.filter(Boolean);
}
